'use client';

import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Blockquote, Button, Progress, Spinner } from '@ui';
import { BelegDropzone, DocumentPreview } from 'components/UserInvoice/BelegAssistant';
import { useConfirmedAction } from 'hooks/useConfirmedAction';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BulkSetBar from './BulkSetBar';
import ReviewTable from './ReviewTable';
import { ColumnKey, isBookable, patchFor } from './types';
import useBookingImportDocuments from './useBookingImportDocuments';

/**
 * Rows per commit request. Every row fires BookingCreated and maybe an invoice, and Vapor cuts
 * a request off after 30s, so the run is chunked here in the browser.
 */
const BLOCK_SIZE = 5;

interface Props {
    eventId: number;
    /** Called after bookings were created, so the list behind the modal can reload. */
    onBooked?: () => void;
}

interface RunSummary {
    created: number;
    failed: number;
    aborted: boolean;
}

/** Upload registration forms, check what the AI read, book them in one go. */
const DocumentImport = ({ eventId, onBooked }: Props) => {
    const { t } = useTranslation('bookings');
    const {
        basePath,
        documents,
        fields,
        isLoading,
        pendingUploads,
        uploadFiles,
        updateDocument,
        bulkUpdate,
        retryDocument,
        discardDocument,
        commit,
        removeDocuments,
        reload
    } = useBookingImportDocuments(eventId);

    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [activeId, setActiveId] = useState<number | null>(null);
    const [progress, setProgress] = useState<number | null>(null);
    const [summary, setSummary] = useState<RunSummary | null>(null);

    const confirmedAction = useConfirmedAction({
        header: t('booking.documentImport.commit.confirmHeader'),
        message: t('booking.documentImport.commit.confirmMessage')
    });

    const apiHost = useMemo(() => process.env.NEXT_PUBLIC_API_HOST ?? '', []);
    const activeDocument = documents.find((doc) => doc.id === activeId) ?? null;

    const readingCount =
        documents.filter((doc) => 'pending' === doc.status || 'processing' === doc.status).length +
        pendingUploads.length;
    const totalCount = documents.length + pendingUploads.length;

    // With a selection only the selected rows are booked, otherwise every bookable one.
    const toBook = useMemo(
        () => documents.filter((doc) => isBookable(doc) && (selected.size === 0 || selected.has(doc.id))),
        [documents, selected]
    );
    const blockedCount = documents.filter(
        (doc) => !isBookable(doc) && (selected.size === 0 || selected.has(doc.id))
    ).length;

    const toggle = useCallback(
        (id: number) =>
            setSelected((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
            }),
        []
    );

    const handleChange = useCallback(
        (id: number, key: ColumnKey, value: any) => void updateDocument(id, patchFor(key, value)),
        [updateDocument]
    );

    const handleDiscard = useCallback(
        (id: number) => {
            void discardDocument(id);
            setSelected((prev) => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            if (activeId === id) setActiveId(null);
        },
        [activeId, discardDocument]
    );

    const run = useCallback(async () => {
        const ids = toBook.map((doc) => doc.id);
        let created = 0;
        let failed = 0;
        setSummary(null);
        setProgress(0);

        for (let offset = 0; offset < ids.length; offset += BLOCK_SIZE) {
            const result = await commit(ids.slice(offset, offset + BLOCK_SIZE));

            // A whole block failing (network, permission) stops the run rather than hiding the cause.
            if (!result) {
                setProgress(null);
                setSummary({ created, failed: ids.length - created, aborted: true });
                void reload();
                if (created > 0) onBooked?.();
                return;
            }

            created += result.created.length;
            failed += Object.keys(result.failed ?? {}).length;
            void removeDocuments(result.created.map((entry) => entry.document_id));
            setProgress((offset + BLOCK_SIZE) / ids.length);
        }

        setProgress(null);
        setSummary({ created, failed, aborted: false });
        setSelected(new Set());
        void reload();
        if (created > 0) onBooked?.();
    }, [commit, onBooked, reload, removeDocuments, toBook]);

    if (isLoading) {
        return (
            <div className="tw-flex tw-justify-center tw-p-8">
                <Spinner />
            </div>
        );
    }

    return (
        <div className="tw-flex tw-flex-col tw-gap-4">
            <p className="tw-m-0 tw-text-sm tw-text-accents-7">{t('booking.documentImport.intro')}</p>

            <BelegDropzone onFiles={(files) => void uploadFiles(files)} isDisabled={progress !== null} />

            {totalCount > 0 && readingCount > 0 && (
                <div className="tw-flex tw-flex-col tw-gap-1">
                    <span className="tw-text-sm tw-text-accents-7">
                        {t('booking.documentImport.reading', { done: totalCount - readingCount, total: totalCount })}
                    </span>
                    <Progress size="sm" color="success" progress={(totalCount - readingCount) / totalCount} />
                </div>
            )}

            {summary && (
                <Blockquote color={summary.aborted || summary.failed > 0 ? 'warning' : 'success'}>
                    {t('booking.documentImport.commit.summary', { created: summary.created, failed: summary.failed })}
                    {summary.aborted && ` ${t('booking.documentImport.commit.aborted')}`}
                </Blockquote>
            )}

            {documents.length > 0 && (
                <>
                    <BulkSetBar
                        fields={fields}
                        selectedCount={selected.size}
                        totalCount={documents.length}
                        onApply={(patch, onlySelected) =>
                            void bulkUpdate(onlySelected ? [...selected] : documents.map((doc) => doc.id), patch)
                        }
                    />

                    <div className="tw-flex tw-gap-4">
                        <div className="tw-min-w-0 tw-flex-1">
                            <ReviewTable
                                documents={documents}
                                fields={fields}
                                selected={selected}
                                activeId={activeId}
                                onToggle={toggle}
                                onToggleAll={(checked) =>
                                    setSelected(checked ? new Set(documents.map((doc) => doc.id)) : new Set())
                                }
                                onPreview={(id) => setActiveId((prev) => (prev === id ? null : id))}
                                onChange={handleChange}
                                onRetry={(id) => void retryDocument(id)}
                                onDiscard={handleDiscard}
                            />
                        </div>

                        {activeDocument && (
                            <div className="tw-sticky tw-top-0 tw-flex tw-h-[70vh] tw-w-[40%] tw-shrink-0 tw-flex-col tw-gap-2">
                                <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                                    <span className="tw-truncate tw-text-sm">{activeDocument.original_name}</span>
                                    <Button
                                        size="sm"
                                        flat
                                        color="light"
                                        aria-label={t('booking.documentImport.actions.closePreview')}
                                        onPress={() => setActiveId(null)}
                                        icon={<FontAwesomeIcon icon={faXmark} />}
                                    />
                                </div>
                                <DocumentPreview
                                    key={`preview-${activeDocument.id}`}
                                    className="tw-min-h-0 tw-flex-1"
                                    mimeType={activeDocument.mime_type}
                                    url={`${apiHost}${basePath}/documents/${activeDocument.id}/file`}
                                />
                            </div>
                        )}
                    </div>

                    {progress !== null ? (
                        <div className="tw-flex tw-flex-col tw-gap-2">
                            <span className="tw-text-sm">{t('booking.documentImport.commit.running')}</span>
                            <Progress color="success" progress={Math.min(1, progress)} />
                        </div>
                    ) : (
                        <div className="tw-flex tw-flex-col tw-gap-2">
                            {blockedCount > 0 && (
                                <span className="tw-text-sm tw-text-accents-7">
                                    {t('booking.documentImport.commit.blocked', { count: blockedCount })}
                                </span>
                            )}
                            <Button
                                block
                                isDisabled={toBook.length === 0 || readingCount > 0}
                                onPress={() => confirmedAction(() => void run())}
                            >
                                {t('booking.documentImport.commit.submit', { count: toBook.length })}
                            </Button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default DocumentImport;
